import { useEffect, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { MangoProvider } from '@humanforce/mango-ui-react';
import { authService } from './auth/service';
import { LoginPage } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { UnauthenticatedLayout } from './components/auth/UnauthenticatedLayout';
import { MainLayout } from './components/layout/MainLayout';
import { itemPath, navSections } from './navigation/navConfig';
import { pageRegistry } from './pages';
import { HomePage } from './pages/HomePage';
import { PayRunDetailPage } from './pages/PayRunDetailPage';
import { OrganisationPage } from './pages/OrganisationPage';

interface RouteConfig {
  key: string;
  path: string;
  element: ReactElement;
}

// Build the route table from the data-driven navigation config so adding or
// reshaping navigation never requires hand-editing routes here.
const buildItemRoutes = (): RouteConfig[] => {
  const routes: RouteConfig[] = [];
  for (const section of navSections)
    for (const item of section.items ?? []) {
      const Page = pageRegistry[itemPath(section, item)];
      if (!Page) continue;
      const rel = `${section.slug}/${item.slug}`;
      if (item.subItems?.length) {
        // Item route redirects to the first tab; each sub-item tab is URL-addressable.
        routes.push({ key: rel, path: rel, element: <Navigate to={item.subItems[0].slug} replace /> });
        routes.push({ key: `${rel}/*`, path: `${rel}/*`, element: <Page /> });
      } else {
        routes.push({ key: rel, path: rel, element: <Page /> });
      }
    }
  return routes;
};

const protectedElement = (element: ReactElement) => (
  <ProtectedRoute>
    <MainLayout>{element}</MainLayout>
  </ProtectedRoute>
);

export const App = () => {
  useEffect(() => {
    void authService.initFromStorage();
  }, []);

  return (
    <MangoProvider>
      <Routes>
        <Route path="/login" element={<UnauthenticatedLayout><LoginPage /></UnauthenticatedLayout>} />
        <Route path="/" element={protectedElement(<HomePage />)} />
        {buildItemRoutes().map(({ key, path, element }) => (
          <Route key={key} path={path} element={protectedElement(element)} />
        ))}
        <Route
          path="payroll/pay-runs/:payId"
          element={protectedElement(<PayRunDetailPage />)}
        />
        <Route path="maintenance/organisation/*" element={protectedElement(<OrganisationPage />)} />
        <Route path="maintenance/organisations/:organisationId/:tab" element={protectedElement(<OrganisationPage />)} />
        <Route path="maintenance/organisations/:organisationId/business-records/:businessRecordId/:tab" element={protectedElement(<OrganisationPage />)} />
        <Route path="*" element={protectedElement(<Navigate to="/" replace />)} />
      </Routes>
    </MangoProvider>
  );
};
